/**
 * SchedulePageV2 — destination for the sidebar's "Schedule" item and the
 * Home page's "View calendar" link. A calendar-style view merging four kinds
 * of scheduled things: one-time scheduled agent/chain runs
 * (agentRunAPI.getScheduled — carry an exact time), task due-dates
 * (taskService.getTasks — day-level only), recurring flow schedules
 * (flowScheduleAPI.listAll — nextRunAt), and recurring agent schedules
 * (customAgentAPI.list, filtered to cadence:"recurring" — nextRunAt).
 *
 * Layout: a persistent month mini-calendar + type filters on the left to
 * jump to any date, a denser week agenda on the right, and a "+ Schedule"
 * button that opens ScheduleEntryModal (routes to AddTaskModal or
 * ScheduleRunModal depending on what's being scheduled).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Workflow, CheckSquare, CalendarClock, Repeat, Plus, RotateCw, Pencil, Trash2, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import AppShellV2, { T2 } from './AppShellV2.tsx';
import MiniCalendar, { isSameDay, dateKey } from './MiniCalendar.tsx';
import ScheduleEntryModal from '../modals/ScheduleEntryModal.tsx';
import ScheduleEditPopover from './ScheduleEditPopover.tsx';
import { agentRunAPI, flowScheduleAPI, customAgentAPI, calendarAPI } from '../../services/api.js';
import { taskService } from '../../services/taskApi.ts';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function getWeekDates(date: Date): Date[] {
  const week: Date[] = [];
  const start = new Date(date);
  start.setDate(date.getDate() - (date.getDay() === 0 ? 6 : date.getDay() - 1)); // Monday-first
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    week.push(d);
  }
  return week;
}

/** Same 42-cell Mon-first grid MiniCalendar renders, used here to bound the month-range fetch. */
function monthGridRange(month: Date): { from: Date; to: Date } {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const leadDays = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const from = new Date(first);
  from.setDate(first.getDate() - leadDays);
  const to = new Date(from);
  to.setDate(from.getDate() + 41);
  return { from, to };
}

type AgendaType = 'task' | 'agent' | 'workflow' | 'recurring-agent' | 'recurring-workflow';

type AgendaItem = {
  id: string;
  type: AgendaType;
  name: string;
  status: string;
  time: string | null; // ISO timestamp for timed items, null for all-day tasks
  at: string; // ISO date the item sits on (for tasks: the due date)
};

const TYPE_META: Record<AgendaType, { label: string; color: string; bg: string; Icon: React.ComponentType<{ size?: number }> }> = {
  agent: { label: 'Agent run', color: '#0F9D58', bg: 'var(--lyra-color-surface-2)', Icon: CalendarClock },
  workflow: { label: 'Workflow run', color: 'var(--lyra-color-primary)', bg: 'var(--app-primary-soft)', Icon: Workflow },
  task: { label: 'Task', color: 'var(--lyra-color-primary)', bg: 'var(--app-primary-soft)', Icon: CheckSquare },
  'recurring-agent': { label: 'Recurring agent', color: 'var(--lyra-color-warning)', bg: 'var(--lyra-color-warning-bg)', Icon: Repeat },
  'recurring-workflow': { label: 'Recurring workflow', color: '#0891B2', bg: 'var(--lyra-color-surface-2)', Icon: Repeat },
};

const ALL_TYPES: AgendaType[] = ['task', 'agent', 'workflow', 'recurring-agent', 'recurring-workflow'];

const actBtn: React.CSSProperties = { border: 'none', background: 'none', cursor: 'pointer', color: T2.ink3, padding: 4, borderRadius: 6, display: 'inline-flex', alignItems: 'center' };

export default function SchedulePageV2({ onLogout }: { onLogout?: () => void }) {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [scheduledRuns, setScheduledRuns] = useState<any[]>([]);
  const [rangeTasks, setRangeTasks] = useState<any[]>([]);
  const [flowSchedules, setFlowSchedules] = useState<any[]>([]);
  const [recurringAgents, setRecurringAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleTypes, setVisibleTypes] = useState<Set<AgendaType>>(new Set(ALL_TYPES));
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState<boolean | null>(null);

  // Same 768px breakpoint AppShellV2/Layout.tsx use — stacks the sidebar
  // above the agenda and tightens padding on narrow screens.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    calendarAPI.status()
      .then((s: any) => { if (!cancelled) setCalendarConnected(!!s?.connected); })
      .catch(() => { if (!cancelled) setCalendarConnected(false); });
    return () => { cancelled = true; };
  }, []);

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);

  // Calendar follows the selected date's month (week-nav, Today, and calendar
  // clicks all funnel through selectedDate, so this stays in sync everywhere).
  useEffect(() => {
    setCalendarMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
  }, [selectedDate]);

  const loadAll = useCallback((silent?: boolean | unknown) => {
    if (silent !== true) setLoading(true);
    const { from, to } = monthGridRange(calendarMonth);
    Promise.allSettled([
      agentRunAPI.getScheduled(),
      taskService.getTasks({ from: from.toISOString(), to: to.toISOString() }),
      flowScheduleAPI.listAll(),
      customAgentAPI.list(),
    ]).then(([runsRes, tasksRes, flowsRes, agentsRes]) => {
      setScheduledRuns(runsRes.status === 'fulfilled' && Array.isArray((runsRes.value as any)?.runs) ? (runsRes.value as any).runs : []);
      setRangeTasks(tasksRes.status === 'fulfilled' && Array.isArray(tasksRes.value) ? tasksRes.value : []);
      setFlowSchedules(flowsRes.status === 'fulfilled' && Array.isArray((flowsRes.value as any)?.schedules) ? (flowsRes.value as any).schedules : []);
      const agentsVal: any = agentsRes.status === 'fulfilled' ? agentsRes.value : null;
      const allAgents = Array.isArray(agentsVal) ? agentsVal : (agentsVal?.agents || []);
      setRecurringAgents(allAgents.filter((a: any) => a.cadence === 'recurring' && a.nextRunAt));
      setLoading(false);
    });
  }, [calendarMonth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Build one AgendaItem per scheduled thing, each tagged with a Date ──────
  const allItems = useMemo(() => {
    const items: { item: AgendaItem; date: Date }[] = [];

    rangeTasks.forEach((t: any) => {
      if (!t.dueDate) return;
      items.push({ date: new Date(t.dueDate), item: { id: t._id, type: 'task', name: t.title || 'Task', status: t.status, time: null, at: t.dueDate } });
    });

    scheduledRuns.forEach((r: any) => {
      if (!r.scheduledAt) return;
      items.push({
        date: new Date(r.scheduledAt),
        item: { id: r._id, type: r.chainId ? 'workflow' : 'agent', name: r.agentName || 'Scheduled run', status: 'scheduled', time: r.scheduledAt, at: r.scheduledAt },
      });
    });

    flowSchedules.forEach((f: any) => {
      if (!f.nextRunAt || f.enabled === false) return;
      items.push({
        date: new Date(f.nextRunAt),
        item: { id: f._id, type: 'recurring-workflow', name: f.flowName || 'Workflow', status: 'recurring', time: f.nextRunAt, at: f.nextRunAt },
      });
    });

    recurringAgents.forEach((a: any) => {
      items.push({
        date: new Date(a.nextRunAt),
        item: { id: a._id, type: 'recurring-agent', name: a.name || 'Agent', status: 'recurring', time: a.nextRunAt, at: a.nextRunAt },
      });
    });

    return items.filter(({ item }) => visibleTypes.has(item.type));
  }, [rangeTasks, scheduledRuns, flowSchedules, recurringAgents, visibleTypes]);

  // ── Calendar dot-marking across the whole visible grid ─────────────────────
  const markedDates = useMemo(() => {
    const m = new Map<string, number>();
    allItems.forEach(({ date }) => {
      const k = dateKey(date);
      m.set(k, (m.get(k) || 0) + 1);
    });
    return m;
  }, [allItems]);

  // ── Agenda for the currently selected week ──────────────────────────────────
  const itemsByDay = useMemo(() => {
    const byDay: AgendaItem[][] = Array(7).fill(null).map(() => []);
    allItems.forEach(({ date, item }) => {
      const idx = weekDates.findIndex((d) => isSameDay(d, date));
      if (idx === -1) return;
      byDay[idx].push(item);
    });
    byDay.forEach((items) => items.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return -1;
      if (!b.time) return 1;
      return new Date(a.time).getTime() - new Date(b.time).getTime();
    }));
    return byDay;
  }, [allItems, weekDates]);

  const totalThisWeek = itemsByDay.reduce((sum, d) => sum + d.length, 0);
  const today = new Date();

  // ── Edit / remove a schedule entry (never the task or agent itself) ──────────
  const [editing, setEditing] = useState<AgendaItem | null>(null);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const rowKey = (it: AgendaItem) => `${it.type}-${it.id}`;

  const moveItem = useCallback(async (it: AgendaItem, next: Date) => {
    const iso = next.toISOString();
    if (it.type === 'agent' || it.type === 'workflow') {
      if (next.getTime() <= Date.now()) { toast.error('Pick a time in the future'); return; }
      const before = scheduledRuns;
      setScheduledRuns((rs) => rs.map((r) => (r._id === it.id ? { ...r, scheduledAt: iso } : r)));   // shows at once
      setEditing(null);
      try { await agentRunAPI.rescheduleRun(it.id, iso); toast.success('Rescheduled'); }
      catch (e: any) { setScheduledRuns(before); toast.error(e?.response?.data?.error || 'Could not reschedule'); }
    } else if (it.type === 'task') {
      const before = rangeTasks;
      setRangeTasks((ts) => ts.map((t) => (t._id === it.id ? { ...t, dueDate: iso } : t)));
      setEditing(null);
      try { await taskService.updateTask(it.id, { dueDate: iso } as any); toast.success('Rescheduled'); }
      catch { setRangeTasks(before); toast.error('Could not reschedule'); }
    }
    setSelectedDate(next);   // follow it to the week it moved to
    loadAll(true);
  }, [scheduledRuns, rangeTasks, loadAll]);

  const removeItem = useCallback(async (it: AgendaItem) => {
    setConfirmKey(null);
    const snap = { scheduledRuns, rangeTasks, flowSchedules, recurringAgents };
    try {
      if (it.type === 'task') {
        setRangeTasks((ts) => ts.map((t) => (t._id === it.id ? { ...t, dueDate: null } : t)));   // clears the date; the task stays
        await taskService.updateTask(it.id, { dueDate: null } as any);
      } else if (it.type === 'agent' || it.type === 'workflow') {
        setScheduledRuns((rs) => rs.filter((r) => r._id !== it.id));   // cancels the run; the agent stays
        await agentRunAPI.cancelScheduled(it.id);
      } else if (it.type === 'recurring-workflow') {
        setFlowSchedules((fs) => fs.filter((f) => f._id !== it.id));
        await flowScheduleAPI.remove(it.id);
      } else {
        setRecurringAgents((as) => as.filter((a) => a._id !== it.id));   // stops repeating; the agent stays
        await customAgentAPI.update(it.id, { cadence: 'one_shot' });
      }
      toast.success('Removed from schedule');
      loadAll(true);
    } catch (e: any) {
      setScheduledRuns(snap.scheduledRuns); setRangeTasks(snap.rangeTasks); setFlowSchedules(snap.flowSchedules); setRecurringAgents(snap.recurringAgents);
      toast.error(e?.response?.data?.error || 'Could not remove');
    }
  }, [scheduledRuns, rangeTasks, flowSchedules, recurringAgents, loadAll]);

  const toggleType = (t: AgendaType) => {
    setVisibleTypes((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  };

  const handleSync = useCallback(async () => {
    if (syncing) return;
    let connected = calendarConnected;
    try {
      const status: any = await calendarAPI.status();
      connected = !!status?.connected;
      setCalendarConnected(connected);
    } catch {
      connected = false;
      setCalendarConnected(false);
    }
    if (!connected) {
      navigate('/integrations#calendar');
      return;
    }
    setSyncing(true);
    try {
      const result: any = await calendarAPI.sync();
      const imported = result?.imported?.length || 0;
      const exported = result?.exported?.length || 0;
      const skipped = result?.skipped?.length || 0;
      const updated = (result?.updated?.length || 0) + (result?.pulled?.filter((p: any) => p.change === 'updated').length || 0);
      const removed = (result?.deleted?.length || 0) + (result?.pulled?.filter((p: any) => p.change === 'removed').length || 0);
      const failed = result?.errors?.length || 0;
      toast.success(`Google Calendar synced — Imported ${imported} · Exported ${exported} · Updated ${updated} · Removed ${removed}${skipped ? ` · Skipped ${skipped}` : ''}${failed ? ` · ${failed} failed` : ''}`);
      loadAll();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Calendar sync failed');
    } finally {
      setSyncing(false);
    }
  }, [syncing, calendarConnected, navigate, loadAll]);

  return (
    <AppShellV2 pageTitle="Schedule" onLogout={onLogout}>
      <div style={{
        maxWidth: 1180, margin: '0 auto',
        padding: isMobile ? '16px 16px 40px' : '24px 32px 60px',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 24, alignItems: isMobile ? 'stretch' : 'flex-start',
      }}>

        {/* ── Left sidebar: quick-add + calendar-jump ── */}
        <div style={{ width: isMobile ? '100%' : 280, flexShrink: 0, position: isMobile ? 'static' : 'sticky', top: 24 }}>
          <button
            onClick={() => setShowScheduleModal(true)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              border: 'none', background: T2.accent, color: 'var(--app-on-accent)', borderRadius: 10, padding: '10px 0',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 16,
            }}
          >
            <Plus size={14} /> Schedule
          </button>

          <div style={{ border: `1px solid ${T2.border}`, borderRadius: 14, background: T2.surface, padding: 14, marginBottom: 14 }}>
            <MiniCalendar
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              markedDates={markedDates}
            />
          </div>

          <div style={{ border: `1px solid ${T2.border}`, borderRadius: 14, background: T2.surface, padding: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T2.ink3, marginBottom: 8 }}>Show</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ALL_TYPES.map((t) => {
                const meta = TYPE_META[t];
                const on = visibleTypes.has(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleType(t)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none',
                      padding: '4px 2px', cursor: 'pointer', opacity: on ? 1 : 0.4, textAlign: 'left',
                    }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: meta.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: T2.ink2 }}>{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right pane: week agenda ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: T2.ink, marginBottom: 4 }}>Schedule</div>
              <div style={{ fontSize: 13, color: T2.ink3 }}>Agent &amp; workflow runs and task due-dates, in one place.</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <style>{'.sched-row .sched-act { opacity: .45; transition: opacity .12s; } .sched-row:hover .sched-act, .sched-row .sched-act:focus-within { opacity: 1; } .sched-row .sched-act button:hover { background: var(--app-surface-3); color: var(--lyra-color-text); } @keyframes lyraSyncSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes lyraDotPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(52,168,83,0.35); } 50% { box-shadow: 0 0 0 4px rgba(52,168,83,0); } }'}</style>
              <button
                onClick={handleSync}
                disabled={syncing}
                title={
                  syncing ? 'Syncing with Google Calendar…'
                  : calendarConnected ? 'Google Calendar connected — click to sync now'
                  : calendarConnected === false ? 'Google Calendar not connected — click to connect'
                  : 'Checking Google Calendar connection…'
                }
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  border: `1px solid ${calendarConnected ? '#34A853' : T2.border}`,
                  background: calendarConnected ? 'var(--lyra-color-surface-2)' : T2.surface,
                  color: calendarConnected ? '#1E7E34' : T2.ink2,
                  borderRadius: 9, padding: '7px 12px', fontSize: 12.5, fontWeight: 600,
                  cursor: syncing ? 'default' : 'pointer', opacity: syncing ? 0.75 : 1, flexShrink: 0,
                }}
              >
                <svg width="15" height="15" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                <span>{syncing ? 'Syncing…' : 'Google Calendar'}</span>
                <span
                  style={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: calendarConnected === null ? 'var(--app-border-strong)' : calendarConnected ? '#34A853' : 'var(--app-text-3)',
                    animation: calendarConnected ? 'lyraDotPulse 2s ease-in-out infinite' : 'none',
                  }}
                />
                <RotateCw size={13} style={syncing ? { animation: 'lyraSyncSpin 1s linear infinite' } : undefined} />
              </button>
              <button
                onClick={() => navigate('/tools/project-management/weekly-planner')}
                style={{ border: `1px solid ${T2.border}`, background: T2.surface, color: T2.ink2, borderRadius: 9, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
              >
                Open Weekly Planner →
              </button>
            </div>
          </div>

          {/* Week navigator */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => setSelectedDate((d) => { const n = new Date(d); n.setDate(d.getDate() - 7); return n; })}
                style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${T2.border}`, background: T2.surface, borderRadius: 8, cursor: 'pointer' }}
              >
                <ChevronLeft size={14} />
              </button>
              <div style={{ fontSize: 13, fontWeight: 600, color: T2.ink }}>
                {weekDates[0].toLocaleDateString([], { month: 'short', day: 'numeric' })} – {weekDates[6].toLocaleDateString([], { month: 'short', day: 'numeric' })}
              </div>
              <button
                onClick={() => setSelectedDate((d) => { const n = new Date(d); n.setDate(d.getDate() + 7); return n; })}
                style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${T2.border}`, background: T2.surface, borderRadius: 8, cursor: 'pointer' }}
              >
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => setSelectedDate(new Date())}
                style={{ border: 'none', background: 'none', color: T2.accentText, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Today
              </button>
            </div>
            <div style={{ fontSize: 12, color: T2.ink3 }}>{totalThisWeek} item{totalThisWeek === 1 ? '' : 's'} this week</div>
          </div>

          {loading ? (
            <div style={{ fontSize: 13, color: T2.ink3 }}>Loading…</div>
          ) : (
            <div style={{ border: `1px solid ${T2.border}`, borderRadius: 14, background: T2.surface, overflow: 'hidden' }}>
              {weekDates.map((d, idx) => {
                const items = itemsByDay[idx];
                const isToday = isSameDay(d, today);
                const isSelected = isSameDay(d, selectedDate);
                return (
                  <div key={idx} style={{ borderTop: idx === 0 ? 'none' : `1px solid ${T2.border}` }}>
                    <div style={{
                      display: 'flex', alignItems: 'baseline', gap: 8,
                      padding: '8px 14px', background: isSelected ? T2.accentSoft : isToday ? 'var(--lyra-color-surface)' : 'transparent',
                    }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: T2.ink }}>{DAY_NAMES[idx]}</span>
                      <span style={{ fontSize: 11, color: isToday ? T2.accentText : T2.ink3, fontWeight: isToday ? 600 : 400 }}>
                        {d.toLocaleDateString([], { month: 'short', day: 'numeric' })}{isToday ? ' · Today' : ''}
                      </span>
                    </div>

                    {items.length === 0 ? (
                      <div style={{ padding: '4px 14px 10px', fontSize: 11.5, color: T2.ink3, fontStyle: 'italic' }}>Nothing scheduled</div>
                    ) : (
                      items.map((it) => {
                        const meta = TYPE_META[it.type];
                        const Icon = meta.Icon;
                        const done = it.type === 'task' && it.status === 'Done';
                        const recurring = it.type === 'recurring-agent' || it.type === 'recurring-workflow';
                        return (
                          <div
                            key={`${it.type}-${it.id}`}
                            className="sched-row"
                            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 14px' }}
                          >
                            <div style={{ width: 20, height: 20, borderRadius: 6, background: meta.bg, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Icon size={11} />
                            </div>
                            <div style={{ minWidth: 0, flex: 1, fontSize: 12.5, fontWeight: 600, color: done ? T2.ink3 : T2.ink, textDecoration: done ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {it.name}
                            </div>
                            <div style={{ fontSize: 10.5, color: T2.ink3, flexShrink: 0 }}>
                              {recurring ? `↻ ${new Date(it.time!).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : it.time ? new Date(it.time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'All day'}
                            </div>
                            {confirmKey === rowKey(it) ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontSize: 11.5, color: T2.ink2 }}>
                                <span>{it.type === 'task' ? 'Clear the date?' : recurring ? 'Stop repeating?' : 'Remove this run?'}</span>
                                <button type="button" onClick={() => removeItem(it)} aria-label="Confirm remove" title="Confirm" style={{ ...actBtn, color: 'var(--lyra-color-danger)' }}><Check size={14} /></button>
                                <button type="button" onClick={() => setConfirmKey(null)} aria-label="Cancel" title="Cancel" style={actBtn}><X size={14} /></button>
                              </div>
                            ) : (
                              <div className="sched-act" style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                {!recurring && (
                                  <button type="button" onClick={() => setEditing(it)} aria-label={`Reschedule ${it.name}`} title="Reschedule" style={actBtn}><Pencil size={13} /></button>
                                )}
                                <button type="button" onClick={() => setConfirmKey(rowKey(it))} aria-label={`Remove ${it.name} from schedule`} title={it.type === 'task' ? 'Remove from schedule (keeps the task)' : recurring ? 'Stop repeating (keeps the agent)' : 'Remove from schedule (keeps the agent)'} style={actBtn}><Trash2 size={13} /></button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ScheduleEditPopover
          name={editing.name}
          kindLabel={TYPE_META[editing.type].label.toLowerCase()}
          initial={new Date(editing.at)}
          withTime={editing.type === 'agent' || editing.type === 'workflow'}
          onSave={(d) => moveItem(editing, d)}
          onClose={() => setEditing(null)}
        />
      )}
      <ScheduleEntryModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        selectedDate={selectedDate}
        onScheduled={loadAll}
      />
    </AppShellV2>
  );
}
